import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking");

  const isCancel = location.pathname.includes("cancel");

  useEffect(() => {
    if (isCancel) { setStatus("cancel"); return; }
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("cancel"); return; }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/billing/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          checkAuth?.();
          return;
        }
      } catch (e) {}
      if (attempts < 8) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [location, isCancel, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grid-noise px-4">
      <div className="max-w-md w-full rounded-2xl border border-border bg-card p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-primary animate-spin mb-4" />
            <h2 className="text-xl font-display font-bold">Confirming your payment…</h2>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-400 mb-4" />
            <h2 className="text-2xl font-display font-bold mb-2">You're in! 🎉</h2>
            <p className="text-muted-foreground mb-6">Your Pro plan is active with full access. Cancel anytime from Billing.</p>
            <Button data-testid="payment-success-continue" onClick={() => navigate("/dashboard")} className="w-full bg-primary hover:bg-primary/90 text-white">Go to dashboard</Button>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-amber-400 mb-4" />
            <h2 className="text-xl font-display font-bold mb-2">Still processing</h2>
            <p className="text-muted-foreground mb-6">Your payment is taking a moment. You can head back and check shortly.</p>
            <Button onClick={() => navigate("/billing")} variant="outline" className="w-full">Back to billing</Button>
          </>
        )}
        {status === "cancel" && (
          <>
            <XCircle className="h-14 w-14 mx-auto text-rose-400 mb-4" />
            <h2 className="text-2xl font-display font-bold mb-2">Checkout cancelled</h2>
            <p className="text-muted-foreground mb-6">No charge was made. You can upgrade anytime.</p>
            <Button data-testid="payment-cancel-back" onClick={() => navigate("/billing")} className="w-full bg-primary hover:bg-primary/90 text-white">Back to billing</Button>
          </>
        )}
      </div>
    </div>
  );
}
